import {
  businessAcquisitionCostAtom,
  companySaleIncludedAtom,
  companySaleYearAtom,
  includeSellerNoteInHistoricalAtom,
  isConventionalLoanTypeAtom,
  isRealEstateIncludedAtom,
  isRollinDealExpensesAtom,
  isRollinLoanTransactionFeeAtom,
  isRollinSBAGuarantyFeeAtom,
  lenderLineOfCreditTotalAmountAtom,
  loanTransactionFeeAtom,
  realEstateBusinessValueDollarAtom,
  realEstateDollarValueAtom,
  sbaGuarantyFeeAtom,
  sbaLoanConventionalAmortScheduleAtom,
  sbaLoanConventionalHasInterestOnlyPeriodAtom,
  sbaLoanConventionalInterestOnlyPeriodAtom,
  sbaLoanConventionalTermLengthAtom,
  sbaLoanInterestRateAtom,
  sbaLoanStartDateAtom,
  sbaLoanTotalWithOptionalFeesAtom,
  sellersNoteAmortizationScheduleAtom,
  sellersNoteDollarAtom,
  sellersNoteHasStandbyProvisionAtom,
  sellersNoteInterestRateAtom,
  sellersNoteStandbyAccrueInterestInFullStandbyAtom,
  sellersNoteStandbyIsFullStandbyAtom,
  sellersNoteStandbyPeriodAtom,
  sellersNoteStartDateAtom,
  sellersNoteTermLengthAtom,
  totalDealExpensesAtom,
  workingCapitalBuyerCashAtom,
} from "./general";

import { toNumberOrNull } from "@/utils/math";
import { pmt } from "@travishorn/finance";
import { atom } from "jotai";
import { DollarAmount } from "../types";
import {
  sba7aPaymentsMetaFocusAtom,
  sellersNotePaymentsMetaFocusAtom,
} from "./paymentsMeta";
import { PaymentMeta } from "./types";

/////
// SBA Loan
/////

export const sbaLoanAmortizationScheduleAtom = atom((get) => {
  if (get(isConventionalLoanTypeAtom)) {
    return toNumberOrNull(get(sbaLoanConventionalAmortScheduleAtom)) || 0;
  }

  const isRealEstateIncluded = get(isRealEstateIncludedAtom);

  const realEstateVal = Number(get(realEstateDollarValueAtom)) || 0;
  const bizVal = Number(get(realEstateBusinessValueDollarAtom)) || 0;
  const bizAcq = Number(get(businessAcquisitionCostAtom)) || 0;

  const dealExpenses = get(isRollinDealExpensesAtom)
    ? Number(get(totalDealExpensesAtom)) || 0
    : 0;
  const transactionFee = get(isRollinLoanTransactionFeeAtom)
    ? Number(get(loanTransactionFeeAtom)) || 0
    : 0;
  const sbaGuarantyFee = get(isRollinSBAGuarantyFeeAtom)
    ? Number(get(sbaGuarantyFeeAtom)) || 0
    : 0;
  const lenderCredit = Number(get(lenderLineOfCreditTotalAmountAtom)) || 0;
  const workingCapitalBuyerCash = Number(get(workingCapitalBuyerCashAtom) || 0);

  const total =
    bizAcq +
    realEstateVal +
    transactionFee +
    sbaGuarantyFee +
    lenderCredit +
    dealExpenses +
    workingCapitalBuyerCash;

  const bizAcqYears = (bizAcq / total) * 10;
  const realEstateYears = (realEstateVal / total) * 25;
  const dealExpensesYears = (dealExpenses / total) * 10;
  const transactionFeeYears = (transactionFee / total) * 10;
  const sbaGuarantyFeeYears = (sbaGuarantyFee / total) * 10;
  const lenderCreditYears = (lenderCredit / total) * 10;
  const workingCapitalBuyerCashYears = (workingCapitalBuyerCash / total) * 10;

  const adjustedTotal =
    bizAcqYears +
    realEstateYears +
    transactionFeeYears +
    dealExpensesYears +
    sbaGuarantyFeeYears +
    lenderCreditYears +
    workingCapitalBuyerCashYears;

  if (!isRealEstateIncluded) return 10;
  if (realEstateVal / (realEstateVal + bizVal) >= 0.51) return 25;

  return adjustedTotal;
});

export const sbaLoanNumberOfPaymentsPerYearAtom = atom<number>(() => 12);
export const sbaLoanRecurringPaymentAtom = atom<DollarAmount>(0);
export const sbaLoanAmortizationPaymentsAtom = atom((get) => {
  const isCompanySaleIncluded = get(companySaleIncludedAtom);
  const companySaleYear = toNumberOrNull(get(companySaleYearAtom));

  const sbaLoanConventionalInterestOnlyPeriod = get(
    sbaLoanConventionalInterestOnlyPeriodAtom,
  );
  const sbaLoanConventionalTermLength = get(sbaLoanConventionalTermLengthAtom);

  const termLength = get(isConventionalLoanTypeAtom)
    ? Number(sbaLoanConventionalTermLength) || 0
    : undefined;

  const interestOnlyPeriodMonths =
    get(isConventionalLoanTypeAtom) &&
    get(sbaLoanConventionalHasInterestOnlyPeriodAtom)
      ? toNumberOrNull(sbaLoanConventionalInterestOnlyPeriod) || 0
      : undefined;

  return getAmoritizationPayments({
    loanAmount: toNumberOrNull(get(sbaLoanTotalWithOptionalFeesAtom)) || 0,
    interestRate: toNumberOrNull(get(sbaLoanInterestRateAtom)) || 0,
    schedule: toNumberOrNull(get(sbaLoanAmortizationScheduleAtom)) || 0,
    paymentPerYear: get(sbaLoanNumberOfPaymentsPerYearAtom),
    startDate: get(sbaLoanStartDateAtom).toDate(),
    paymentsMeta: get(sba7aPaymentsMetaFocusAtom),
    recurringPayment: toNumberOrNull(get(sbaLoanRecurringPaymentAtom)) || 0,
    companySaleYear: isCompanySaleIncluded
      ? companySaleYear || undefined
      : undefined,
    termLength,
    interestOnlyPeriodMonths,
  });
});

/////
// Sellers Note
/////

export const sellersNoteNumberOfPaymentsPerYearAtom = atom<number>(() => 12);

export const sellersNoteRecurringPaymentAtom = atom<DollarAmount>(0);

export const sellersNoteAmortizationPaymentsAtom = atom((get) => {
  const isCompanySaleIncluded = get(companySaleIncludedAtom);
  const companySaleYear = isCompanySaleIncluded
    ? toNumberOrNull(get(companySaleYearAtom)) || undefined
    : undefined;

  const standbyType = !get(sellersNoteHasStandbyProvisionAtom)
    ? "none"
    : get(sellersNoteStandbyIsFullStandbyAtom)
      ? get(sellersNoteStandbyAccrueInterestInFullStandbyAtom)
        ? "full-accrue"
        : "full"
      : "partial";

  const standbyPeriod = get(sellersNoteHasStandbyProvisionAtom)
    ? toNumberOrNull(get(sellersNoteStandbyPeriodAtom)) || 1
    : undefined;

  const termLength = Number(get(sellersNoteTermLengthAtom)) || 0;

  return getAmoritizationPayments({
    loanAmount: toNumberOrNull(get(sellersNoteDollarAtom)) || 0,
    interestRate: toNumberOrNull(get(sellersNoteInterestRateAtom)) || 0,
    schedule: toNumberOrNull(get(sellersNoteAmortizationScheduleAtom)) || 0,
    paymentPerYear: get(sellersNoteNumberOfPaymentsPerYearAtom),
    startDate: get(sellersNoteStartDateAtom).toDate(),
    paymentsMeta: get(sellersNotePaymentsMetaFocusAtom),
    recurringPayment: toNumberOrNull(get(sellersNoteRecurringPaymentAtom)) || 0,
    standbyPeriod,
    standbyType,
    companySaleYear,
    termLength,
  });
});

const getPaymentsWithPrincipal = (payments: AmortizationPayment[]) => {
  const result: AmortizationPayment[] = [];

  for (let i = 0; i < payments.length; i++) {
    if (payments[i].principal > 0) {
      result.push(payments[i]);
    }
  }

  return result;
};

export const sbaLoanPaymentsWithPrincipalAtom = atom((get) => {
  const payments = get(sbaLoanAmortizationPaymentsAtom);
  return getPaymentsWithPrincipal(payments);
});

export const sellersNotePaymentsWithPrincipalAtom = atom((get) => {
  const payments = get(sellersNoteAmortizationPaymentsAtom);
  const result = getPaymentsWithPrincipal(payments);

  const isExcluded = get(includeSellerNoteInHistoricalAtom) === false;
  if (isExcluded) {
    return result.map((payment) => {
      return {
        ...payment,
        principal: 0,
        interest: 0,
        totalPayment: 0,
        interestPayment: 0,
      };
    });
  }

  return result;
});

export type AmortizationPayment = {
  index: number;
  paymentPeriod: number;
  date: Date;
  yearPeriod: number;
  scheduledPayment: number;
  extraPayment: number;
  totalPayment: number;
  principal: number;
  interest: number;
  endingBalance: number;
  cumulativeInterest: number;
  startBalance: number;
  fixedExtraPayment: number;
  interestPayment: number;
  balloonPayment: number;
  companySalePayment: number;
};

export const getAmoritizationNumberPayments = ({
  schedule,
  paymentPerYear,
}: {
  schedule: number;
  paymentPerYear: number;
}) => {
  const numberOfPayments = Math.ceil(schedule * paymentPerYear) - 1; // Subtract 1 from schedule to account for the first payment
  return numberOfPayments;
};

const getAmoritizationPayments = ({
  loanAmount,
  interestRate,
  schedule,
  paymentPerYear,
  startDate,
  paymentsMeta,
  recurringPayment,
  standbyPeriod,
  standbyType,
  companySaleYear,
  termLength,
  interestOnlyPeriodMonths,
}: {
  loanAmount: number;
  interestRate: number;
  schedule: number;
  paymentPerYear: number;
  startDate: Date;
  paymentsMeta: Record<number, PaymentMeta>;
  recurringPayment?: number;
  standbyPeriod?: number;
  standbyType?: "full" | "partial" | "full-accrue" | "none";
  companySaleYear?: number;
  termLength?: number;
  interestOnlyPeriodMonths?: number;
}) => {
  const numberOfPayments = getAmoritizationNumberPayments({
    schedule,
    paymentPerYear,
  });

  let cumulativeInterest = 0;

  const table: AmortizationPayment[] = [];
  let i = 0;
  while (i >= 0) {
    const paymentPeriod = i + 1;
    const yearPeriod = Math.floor(i / paymentPerYear) + 1;
    const date = addMonths(startDate, i);
    const startBalance = i === 0 ? loanAmount : table[i - 1].endingBalance;
    const isInStandbyPeriod =
      standbyPeriod !== undefined && yearPeriod <= standbyPeriod;
    const isInInterestOnlyPeriod =
      interestOnlyPeriodMonths !== undefined && i < interestOnlyPeriodMonths;
    const extraPayment = paymentsMeta[i]?.extraPayment || 0;
    const fixedExtraPayment = extraPayment || recurringPayment || 0;

    const isYearAfterCompanySold =
      !!companySaleYear && companySaleYear + 1 === yearPeriod;

    if (isYearAfterCompanySold) {
      table.push({
        index: i,
        yearPeriod,
        paymentPeriod,
        date,
        startBalance,
        extraPayment,
        fixedExtraPayment,
        endingBalance: 0,
        scheduledPayment: 0,
        totalPayment: 0,
        principal: 0,
        interest: 0,
        cumulativeInterest,
        interestPayment: 0,
        balloonPayment: 0,
        companySalePayment: 0,
      });
      break;
    }

    let scheduledPayment =
      pmt(interestRate / 12, numberOfPayments, loanAmount) * -1;
    let interest = startBalance * (interestRate / paymentPerYear);
    let principal = scheduledPayment - interest;
    let totalPayment = principal + interest + fixedExtraPayment;

    if (scheduledPayment > startBalance) {
      totalPayment = startBalance + interest;
      scheduledPayment = startBalance;
      principal = startBalance;
    }

    let accruedInterestInStandby = 0;
    let interestPayment = interest;

    if (isInStandbyPeriod || isInInterestOnlyPeriod) {
      interestPayment = 0;

      if (standbyType === "full") {
        interest = 0;
        principal = 0;
        scheduledPayment = 0;
        totalPayment = fixedExtraPayment;
        interestPayment = 0;
      }
      if (standbyType === "full-accrue") {
        principal = 0;
        scheduledPayment = 0;
        totalPayment = fixedExtraPayment;
        accruedInterestInStandby = interest;
      }
      if (standbyType === "partial" || isInInterestOnlyPeriod) {
        totalPayment -= principal;
        totalPayment += fixedExtraPayment;
        principal = 0;
        scheduledPayment = 0;
        interestPayment = interest;
      }
    }

    let endingBalance = Math.max(
      0,
      startBalance - principal - fixedExtraPayment + accruedInterestInStandby,
    );

    cumulativeInterest += interest;

    const isLastPeriodOfTermLength =
      !!termLength && i + 1 === termLength * paymentPerYear;

    let balloonPayment = isLastPeriodOfTermLength ? endingBalance : 0;
    if (isLastPeriodOfTermLength) endingBalance = 0;

    let companySalePayment = 0;
    if (companySaleYear && yearPeriod === companySaleYear) {
      companySalePayment = balloonPayment;
      balloonPayment = 0;
    }

    table.push({
      index: i,
      yearPeriod,
      paymentPeriod,
      date,
      startBalance,
      extraPayment,
      fixedExtraPayment,
      endingBalance,
      scheduledPayment,
      totalPayment,
      principal,
      interest,
      cumulativeInterest,
      interestPayment,
      balloonPayment,
      companySalePayment,
    });

    if (endingBalance < 0.001 || i > 10000) {
      break;
    }

    i++;
  }

  return table;
};

const addMonths = (date: Date, months: number) => {
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() + months);
  return newDate;
};
